import React, { useRef, useState, useEffect } from "react";
import { Typography, Button, Tooltip, message, Card, Descriptions } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { EditOutlined, DeleteOutlined, PlusOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import api from "src/config/api";
import {
  Form,
  FormDatePicker,
  FormNumber,
  FormText,
} from "src/components/Form";
import eventBus from "src/common/eventBus";
import Table from "src/components/Table/Table";
import { formatDate } from "src/shared/dateTimeUtils";
import Modal from "antd/lib/modal/Modal";

const BikeExpenses = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const tableRef = useRef();
  const [bikeDetails, setBikeDetails] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentExpense, setCurrentExpense] = useState(null);
  const [formKey, setFormKey] = useState(0);

  useEffect(() => {
    const fetchBikeDetails = async () => {
      try {
        const response = await api.get(`bikes/${id}`);
        setBikeDetails(response.data);
      } catch (error) {
        message.error("Failed to fetch bike details");
      }
    };
    
    fetchBikeDetails();
  }, [id]);

  const showAddModal = () => {
    setIsEditMode(false);
    setCurrentExpense({
      expenseDate: new Date().toISOString()
    });
    setIsModalVisible(true);
    setFormKey(prevKey => prevKey + 1);
  };

  const showEditModal = (expense) => {
    setIsEditMode(true);
    setCurrentExpense(expense);
    setIsModalVisible(true);
    setFormKey(prevKey => prevKey + 1);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };

  const handleSubmit = async (data) => {
    try {
      if (isEditMode) {
        await api.put(`bikes/expenses/${currentExpense.id}`, data);
        message.success("Expense updated successfully");
      } else {
        await api.post(`bikes/${id}/expenses`, data);
        message.success("Expense added successfully");
      }
      setIsModalVisible(false);
      tableRef.current.refresh();
      
      // Refresh bike details to update financial information
      const response = await api.get(`bikes/${id}`);
      setBikeDetails(response.data);
    } catch (error) {
      message.error(error.response?.data?.message || "Operation failed");
    }
  };

  const actions = [
    <Button
      icon={<ArrowLeftOutlined />}
      onClick={() => navigate("/bikes")}
    >
      Back to Bikes
    </Button>,
    <Button
      icon={<PlusOutlined />}
      onClick={showAddModal}
      type="primary"
    >
      Add Expense
    </Button>,
  ];

  const columns = [
    {
      dataIndex: "expenseDate",
      title: "Date",
      width: "120px",
      render: (date) => formatDate(date),
      defaultSortOrder: "descend",
    },
    {
      dataIndex: "expenseType",
      title: "Type",
      width: "200px",
    },
    {
      dataIndex: "cost",
      title: "Cost",
      width: "120px",
      render: (cost) => formatCurrency(cost),
    },
    {
      dataIndex: "actions",
      title: "Actions",
      width: "120px",
      render: (_, row) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              onClick={() => showEditModal(row)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              icon={<DeleteOutlined />}
              danger
              onClick={() => {
                eventBus.emit("dialog", {
                  type: "confirm",
                  message: `Are you sure to delete this expense?`,
                  onOk: async () => {
                    await api.delete(`bikes/expenses/${row.id}`);
                  },
                  onFinish: async () => {
                    tableRef.current.refresh();
                    // Refresh bike details to update financial information
                    const response = await api.get(`bikes/${id}`);
                    setBikeDetails(response.data);
                  },
                });
              }}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  // Helper function to format currency in Vietnamese style
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '';
    return `${Number(amount).toLocaleString('vi-VN')} VND`;
  };

  return (
    <>
      <Typography.Title level={4} className="mb-4">
        Bike Expenses
      </Typography.Title>

      {bikeDetails && (
        <Card className="mb-4">
          <Descriptions
            title={`${bikeDetails.bikeName} (${
              bikeDetails.licenseNumber || "No License"
            })`}
            bordered
          >
            <Descriptions.Item label="Status">
              {bikeDetails.status}
            </Descriptions.Item>
            <Descriptions.Item label="Owner">
              {bikeDetails.owner || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Last Oil Change">
              {formatDate(bikeDetails.lastOilChange) || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Purchase Price">
              {formatCurrency(bikeDetails.purchasePrice)}
            </Descriptions.Item>
            <Descriptions.Item label="Money In">
              {formatCurrency(bikeDetails.moneyIn)}
            </Descriptions.Item>
            <Descriptions.Item label="Money Out">
              {formatCurrency(bikeDetails.moneyOut)}
            </Descriptions.Item>
            <Descriptions.Item label="Profit">
              {formatCurrency(bikeDetails.profit)}
            </Descriptions.Item>
            <Descriptions.Item label="KM Run">
              {bikeDetails.kilometersRun || "N/A"}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      <Table
        ref={tableRef}
        url={`bikes/${id}/expenses`}
        actions={actions}
        columns={columns}
      ></Table>

      <Modal
        title={isEditMode ? "Edit Expense" : "Add Expense"}
        open={isModalVisible}
        onCancel={handleModalCancel}
        footer={null}
        destroyOnClose={true}
      >
        <Form
        //   key={formKey}
          onSubmit={handleSubmit}
          layout="vertical"
          defaultValues={currentExpense || {}}
        >
          <FormText
            label="Expense Type"
            placeholder="e.g., Oil Change, New Tire, Repair"
            name="expenseType"
            required
          />
          <FormDatePicker
            label="Date"
            placeholder="Expense Date"
            name="expenseDate"
            required
          />
          <FormNumber
            label="Cost"
            placeholder="Cost in VND"
            name="cost"
            required
          />
          <Button
            type="primary"
            htmlType="submit"
            style={{ marginTop: "16px" }}
          >
            {isEditMode ? "Update" : "Add"}
          </Button>
        </Form>
      </Modal>
    </>
  );
};

export default BikeExpenses; 